<template>
  <div
    class="hero-banner"
    :class="{ 'is-carousel': isCarousel }"
    @mouseenter="pauseCarousel"
    @mouseleave="resumeCarousel"
  >
    <div
      v-if="isCarousel"
      class="banner-carousel-shell"
    >
      <div
        class="banner-carousel"
        @pointerdown="handlePointerDown"
        @pointermove="handlePointerMove"
        @pointerup="handlePointerUp"
        @pointercancel="handlePointerCancel"
      >
        <div
          class="banner-track"
          :class="{ dragging: isDragging }"
          :style="trackStyle"
        >
          <div
            v-for="(slide, index) in carouselSlides"
            :key="slide"
            class="banner-slide"
            :aria-hidden="index !== activeIndex"
          >
            <img
              :src="slide"
              :alt="$t('forms.lotteryBanner')"
              draggable="false"
            >
          </div>
        </div>

        <button
          class="carousel-control previous"
          type="button"
          :aria-label="$t('common.previous')"
          @pointerdown.stop
          @click.stop.prevent="previousSlide"
        >
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              d="M14.5 6.5 9 12l5.5 5.5"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <button
          class="carousel-control next"
          type="button"
          :aria-label="$t('common.next')"
          @pointerdown.stop
          @click.stop.prevent="nextSlide"
        >
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              d="M9.5 6.5 15 12l-5.5 5.5"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>

      <div
        class="carousel-dots"
        role="tablist"
        @pointerdown.stop
      >
        <button
          v-for="(_, index) in carouselSlides"
          :key="index"
          class="carousel-dot"
          :class="{ active: index === activeIndex }"
          type="button"
          role="tab"
          :aria-selected="index === activeIndex"
          :aria-label="`${$t('common.view')} ${$t('forms.lotteryBanner')} ${index + 1}`"
          @click.stop.prevent="goToSlide(index)"
        />
      </div>
    </div>

    <picture v-else>
      <source media="(min-width: 768px)" :srcset="heroBanner" />
      <img
        :src="heroBannerMobile"
        :alt="$t('forms.lotteryBanner')"
      >
    </picture>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useTheme } from '@/composables/useTheme'
import { useGameStore } from '@/stores/game'

const { currentTheme, currentThemeName, getAssetPath } = useTheme()
const gameStore = useGameStore()

const resolveThemeAssetPath = (path: string): string => {
  const locale = new URLSearchParams(window.location.search).get('locale') || 'en'
  let resolvedPath = path.replace('{locale}', locale).replace('/locale/en/', `/locale/${locale}/`)

  if (resolvedPath.includes('/themes/')) {
    resolvedPath = resolvedPath.replace(/\/themes\/[^/]+/, `/themes/${currentThemeName.value}`)
  }

  return resolvedPath
}

const uploadedBanner = (assetType: 'large' | 'small'): string => {
  const assets = gameStore.getBetSettings?.theme_assets
  const locale = new URLSearchParams(window.location.search).get('locale') || 'en'
  const localized = assets?.banners?.[locale]?.[assetType]?.url
  const englishFallback = assets?.banners?.en?.[assetType]?.url
  return localized || englishFallback || ''
}

const heroBanner = computed(() => uploadedBanner('large') || getAssetPath('heroBanner') || '/img/images/banners/banner-lg.png')
const heroBannerMobile = computed(() => uploadedBanner('small') || uploadedBanner('large') || getAssetPath('heroBannerMobile') || '/img/images/banners/banner-sm.png')

const activeIndex = ref(0)
const isDragging = ref(false)
const dragStartX = ref(0)
const dragDeltaX = ref(0)
let autoPlayTimer: number | undefined

const carouselSlides = computed(() => {
  const configuredSlides = currentTheme.value.assets.heroBannerSlides
  if (Array.isArray(configuredSlides) && configuredSlides.length > 0) {
    return configuredSlides.map(resolveThemeAssetPath).filter(Boolean)
  }

  return []
})

const isCarousel = computed(() => carouselSlides.value.length > 1)

const trackStyle = computed(() => ({
  transform: `translateX(calc(-${activeIndex.value * 100}% + ${dragDeltaX.value}px))`
}))

const goToSlide = (index: number, restartAutoPlay = true) => {
  const total = carouselSlides.value.length
  if (!total) return
  activeIndex.value = (index + total) % total
  if (restartAutoPlay) resumeCarousel()
}

const nextSlide = () => goToSlide(activeIndex.value + 1)
const previousSlide = () => goToSlide(activeIndex.value - 1)

const pauseCarousel = () => {
  if (autoPlayTimer) {
    window.clearInterval(autoPlayTimer)
    autoPlayTimer = undefined
  }
}

const resumeCarousel = () => {
  pauseCarousel()
  if (isCarousel.value) {
    autoPlayTimer = window.setInterval(nextSlide, 6000)
  }
}

const handlePointerDown = (event: PointerEvent) => {
  if (!isCarousel.value) return
  pauseCarousel()
  isDragging.value = true
  dragStartX.value = event.clientX
  dragDeltaX.value = 0
  ;(event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId)
}

const handlePointerMove = (event: PointerEvent) => {
  if (!isDragging.value) return
  dragDeltaX.value = event.clientX - dragStartX.value
}

const completeDrag = () => {
  if (!isDragging.value) return
  const threshold = 48
  if (dragDeltaX.value > threshold) {
    goToSlide(activeIndex.value - 1, false)
  } else if (dragDeltaX.value < -threshold) {
    goToSlide(activeIndex.value + 1, false)
  }
  isDragging.value = false
  dragDeltaX.value = 0
  resumeCarousel()
}

const handlePointerUp = () => completeDrag()
const handlePointerCancel = () => {
  isDragging.value = false
  dragDeltaX.value = 0
  resumeCarousel()
}

watch(carouselSlides, (slides) => {
  if (activeIndex.value >= slides.length) activeIndex.value = 0
  if (slides.length > 1) resumeCarousel()
}, { immediate: true })

onMounted(resumeCarousel)
onUnmounted(pauseCarousel)
</script>

<style scoped>
.hero-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  overflow: hidden;
}

.hero-banner.is-carousel {
  overflow: visible;
}

.banner-carousel-shell {
  width: 100%;
}

picture {
  width: 100%;
  height: auto;
  display: block;
}

.banner-carousel {
  position: relative;
  width: 100%;
  overflow: hidden;
  touch-action: pan-y;
  user-select: none;
}

.banner-track {
  display: flex;
  width: 100%;
  transition: transform 0.35s ease;
  will-change: transform;
}

.banner-track.dragging {
  transition: none;
}

.banner-slide {
  flex: 0 0 100%;
  min-width: 100%;
}

img {
  width: 100%;
  height: auto;
  max-width: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  -webkit-user-drag: none;
}

.carousel-control {
  position: absolute;
  top: 50%;
  z-index: 2;
  width: var(--hero-carousel-control-width, 36px);
  height: var(--hero-carousel-control-height, 36px);
  border: 0;
  padding: 0;
  background: var(--hero-carousel-control-bg, var(--accent-color, #ff3304));
  color: var(--hero-carousel-control-color, #ffffff);
  cursor: pointer;
  transform: translateY(-50%);
  display: grid;
  place-items: center;
  box-shadow: var(--hero-carousel-control-shadow, 0 2px 8px rgba(0, 0, 0, 0.18));
}

.carousel-control svg {
  width: var(--hero-carousel-arrow-size, 18px);
  height: var(--hero-carousel-arrow-size, 18px);
  display: block;
}

.carousel-control.previous {
  left: 0;
  border-radius: 0 999px 999px 0;
  padding-right: 2px;
}

.carousel-control.next {
  right: 0;
  border-radius: 999px 0 0 999px;
  padding-left: 2px;
}

.carousel-dots {
  display: flex;
  justify-content: center;
  gap: var(--hero-carousel-dot-gap, 6px);
  padding: var(--hero-carousel-dots-padding, 12px 0 14px);
}

.carousel-dot {
  width: var(--hero-carousel-dot-size, 7px);
  height: var(--hero-carousel-dot-size, 7px);
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: var(--hero-carousel-dot-inactive, rgba(29, 38, 54, 0.35));
  box-shadow: var(--hero-carousel-dot-shadow, none);
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.carousel-dot.active {
  background: var(--hero-carousel-dot-active, var(--accent-color, #ff3304));
  transform: scale(1.15);
}

:global(html.theme-dark) .hero-banner.is-carousel .carousel-dot:not(.active) {
  background: var(--hero-carousel-dot-inactive, rgba(255, 255, 255, 0.55));
  box-shadow: var(--hero-carousel-dot-shadow, 0 0 0 1px rgba(255, 255, 255, 0.18));
}

@media (max-width: 767px) {
  .carousel-control {
    width: var(--hero-carousel-control-width-mobile, 32px);
    height: var(--hero-carousel-control-height-mobile, 32px);
  }

  .carousel-control svg {
    width: var(--hero-carousel-arrow-size-mobile, 16px);
    height: var(--hero-carousel-arrow-size-mobile, 16px);
  }

  .carousel-dots {
    display: none;
  }
}

</style> 
